import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaExclamationTriangle,
  FaFileAlt,
  FaFolder,
  FaFolderOpen,
  FaSearch,
  FaSyncAlt,
  FaTrashAlt,
  FaTimes,
} from "react-icons/fa";
import AdminHeader from "../../components/layout/AdminHeader";
import adminService from "../../services/adminService";

const LOG_ORDER = {
  EARLIEST: "earliest",
  LATEST: "latest",
};

const getErrorMessage = (error, fallback) =>
  error?.data?.message || error?.message || fallback;

const normalizeLogLines = (content = "") => {
  if (!content) {
    return [];
  }

  const lines = content.split(/\r?\n/);
  if (lines.length > 0 && lines[lines.length - 1] === "") {
    lines.pop();
  }

  return lines;
};

const AdminLogs = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedCollection, setSelectedCollection] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [logOrder, setLogOrder] = useState(LOG_ORDER.EARLIEST);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const user = localStorage.getItem("user");

    if (!token || !user) {
      navigate("/admin/login", { replace: true });
      return;
    }

    try {
      const parsedUser = JSON.parse(user);
      if (parsedUser.user_type !== "admin") {
        navigate("/admin/login", { replace: true });
      }
    } catch (parseError) {
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  const resetSelectedLog = useCallback(() => {
    setSelectedCollection("");
    setSelectedLog(null);
    setSearchInput("");
    setAppliedSearch("");
    setConfirmingDelete(false);
  }, []);

  const loadGroups = useCallback(
    async (retainGroup = "", retainCollection = "") => {
      setListLoading(true);
      setError("");

      try {
        const response = await adminService.getSystemLogs();
        const nextGroups = response?.data?.groups || [];
        setGroups(nextGroups);

        if (retainGroup) {
          const nextSelectedGroup = nextGroups.find(
            (item) => item.group === retainGroup
          );

          if (!nextSelectedGroup) {
            setSelectedGroup("");
            resetSelectedLog();
            return;
          }

          if (retainCollection) {
            const stillExists = (nextSelectedGroup.collections || []).some(
              (item) => item.collection === retainCollection
            );
            if (!stillExists) {
              resetSelectedLog();
            }
          }
        }
      } catch (loadError) {
        setError(getErrorMessage(loadError, "Unable to load extraction logs."));
      } finally {
        setListLoading(false);
      }
    },
    [resetSelectedLog]
  );

  const loadSelectedLog = useCallback(async (group, collection) => {
    if (!group || !collection) {
      return;
    }

    setDetailLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await adminService.getSystemLog(group, collection);
      const nextLog = response?.data || null;
      setSelectedLog(nextLog);
    } catch (loadError) {
      setSelectedLog(null);
      setError(getErrorMessage(loadError, "Unable to load the selected log."));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const selectedGroupData = useMemo(
    () => groups.find((item) => item.group === selectedGroup) || null,
    [groups, selectedGroup]
  );
  const selectedGroupCollections = selectedGroupData?.collections || [];

  const logLines = useMemo(
    () => normalizeLogLines(selectedLog?.content),
    [selectedLog]
  );

  const filteredLines = useMemo(() => {
    const query = appliedSearch.trim().toLowerCase();

    if (!query) {
      return logLines;
    }

    return logLines.filter((line) => line.toLowerCase().includes(query));
  }, [appliedSearch, logLines]);

  const displayedLines = useMemo(() => {
    if (logOrder === LOG_ORDER.LATEST) {
      return [...filteredLines].reverse();
    }

    return filteredLines;
  }, [filteredLines, logOrder]);

  const displayedContent = useMemo(
    () => displayedLines.join("\n"),
    [displayedLines]
  );
  const isSearchActive = Boolean(appliedSearch.trim());
  const totalLogCount = groups.reduce((total, group) => total + (group.count || 0), 0);

  const selectedGroupLabel =
    selectedGroupData?.label || selectedLog?.group_label || selectedGroup || "No folder selected";
  const selectedCollectionLabel =
    selectedLog?.collection || selectedCollection || "No log selected";

  const handleGroupClick = (group) => {
    setSelectedGroup(group);
    setMessage("");
    resetSelectedLog();
  };

  const handleCollectionClick = async (collection) => {
    if (!selectedGroup) {
      return;
    }

    setSelectedCollection(collection);
    setConfirmingDelete(false);
    setSearchInput("");
    setAppliedSearch("");
    await loadSelectedLog(selectedGroup, collection);
  };

  const handleRefreshList = () => {
    loadGroups(selectedGroup, selectedCollection);
  };

  const handleRefreshSelected = async () => {
    if (!selectedGroup || !selectedCollection) {
      return;
    }

    setMessage("");
    await Promise.all([
      loadSelectedLog(selectedGroup, selectedCollection),
      loadGroups(selectedGroup, selectedCollection),
    ]);
  };

  const handleDeleteSelected = () => {
    if (!selectedGroup || !selectedCollection || !selectedLog) {
      return;
    }

    setConfirmingDelete(true);
  };

  const handleCancelDelete = () => {
    setConfirmingDelete(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedGroup || !selectedCollection) {
      return;
    }

    setActionLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await adminService.deleteSystemLog(
        selectedGroup,
        selectedCollection
      );
      resetSelectedLog();
      setMessage(response?.message || "Log file deleted.");
      await loadGroups(selectedGroup);
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "Unable to delete the selected log."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setAppliedSearch(searchInput.trim());
  };

  const handleResetSearch = () => {
    setSearchInput("");
    setAppliedSearch("");
  };

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-100">
      <AdminHeader
        title="Extraction Logs"
        subtitle="Browse and delete cron extraction log files by folder."
      />

      <main className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-10 lg:py-8">
        {error ? (
          <div className="mb-5 rounded-xl border border-rose-500/25 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
            {error}
          </div>
        ) : null}
        {message ? (
          <div className="mb-5 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
            {message}
          </div>
        ) : null}

        <section className="mb-6 rounded-xl border border-slate-800 bg-slate-950/70 px-4 py-5 shadow-[0_18px_40px_rgba(2,6,23,0.3)] sm:px-5">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Extraction log browser
                </div>
                <h1 className="mt-1 text-2xl font-semibold tracking-[-0.01em] text-slate-50">
                  Log folders
                </h1>
              </div>

              <div className="flex items-center gap-3">
                <div className="text-sm text-slate-500">
                  {listLoading ? "Loading log folders..." : `${totalLogCount} logs`}
                </div>
                <button
                  type="button"
                  onClick={handleRefreshList}
                  disabled={listLoading}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-900/80 px-3 text-sm font-medium text-slate-200 transition hover:border-sky-500/40 hover:text-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaSyncAlt className={listLoading ? "animate-spin" : ""} />
                  Refresh list
                </button>
              </div>
            </div>

            <div className="overflow-x-auto overflow-y-hidden pb-2 pr-1">
              <div className="flex min-w-max gap-3">
                {listLoading ? (
                  Array.from({ length: 3 }).map((_, index) => (
                    <div
                      key={index}
                      className="flex h-20 min-w-[18rem] items-center gap-3 rounded-xl border border-slate-800 bg-slate-950/60 px-4"
                    >
                      <div className="h-9 w-9 rounded-lg bg-slate-800/80" />
                      <div className="space-y-2">
                        <div className="h-4 w-32 rounded bg-slate-800/80" />
                        <div className="h-3 w-44 rounded bg-slate-800/80" />
                      </div>
                    </div>
                  ))
                ) : groups.length === 0 ? (
                  <div className="rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm text-slate-500">
                    No log folders were returned by the admin API.
                  </div>
                ) : (
                  groups.map((item) => {
                    const isSelected = item.group === selectedGroup;

                    return (
                      <button
                        key={item.group}
                        type="button"
                        onClick={() => handleGroupClick(item.group)}
                        className={`flex min-w-[18rem] shrink-0 items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                          isSelected
                            ? "border-sky-500/40 bg-sky-500/10 text-slate-50"
                            : "border-slate-800 bg-slate-950/60 text-slate-300 hover:border-slate-700 hover:bg-slate-900/60"
                        }`}
                        aria-pressed={isSelected}
                      >
                        <div
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                            isSelected
                              ? "bg-sky-500/15 text-sky-300"
                              : "bg-slate-900 text-slate-500"
                          }`}
                        >
                          {isSelected ? <FaFolderOpen /> : <FaFolder />}
                        </div>
                        <span className="min-w-0">
                          <span className="block break-words text-sm font-semibold">
                            {item.group}
                          </span>
                          <span className="mt-1 block break-words text-xs leading-5 text-slate-500">
                            {item.label} - {item.count || 0} logs
                          </span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div>
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-slate-300">
                  Logs in {selectedGroup ? selectedGroupLabel : "selected folder"}
                </div>
                <div className="text-xs text-slate-500">
                  {selectedGroup ? `${selectedGroupCollections.length} files` : "Select a folder"}
                </div>
              </div>
              <div className="overflow-x-auto overflow-y-hidden pb-2 pr-1">
                <div className="flex min-w-max gap-3">
                  {!selectedGroup ? (
                    <div className="rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm text-slate-500">
                      Select one of the three log folders to view its log files.
                    </div>
                  ) : selectedGroupCollections.length === 0 ? (
                    <div className="rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm text-slate-500">
                      No `.log` files were found in this folder.
                    </div>
                  ) : (
                    selectedGroupCollections.map((item) => {
                      const isSelected = item.collection === selectedCollection;

                      return (
                        <button
                          key={`${item.group}:${item.collection}`}
                          type="button"
                          onClick={() => handleCollectionClick(item.collection)}
                          className={`flex min-w-[14rem] shrink-0 items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                            isSelected
                              ? "border-sky-500/40 bg-sky-500/10 text-slate-50"
                              : "border-slate-800 bg-slate-950/60 text-slate-300 hover:border-slate-700 hover:bg-slate-900/60"
                          }`}
                          aria-pressed={isSelected}
                        >
                          <div
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                              isSelected
                                ? "bg-sky-500/15 text-sky-300"
                                : "bg-slate-900 text-slate-500"
                            }`}
                          >
                            <FaFileAlt />
                          </div>
                          <span className="min-w-0 break-words text-sm font-semibold">
                            {item.collection}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950/70">
          <div className="flex flex-col gap-4 border-b border-slate-800 px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <FaFileAlt className="text-sky-400" />
                <h2 className="break-words text-lg font-semibold text-slate-50">
                  {selectedCollectionLabel}
                </h2>
              </div>
              <div className="mt-2 text-sm text-slate-500">
                {selectedLog
                  ? `Open log content from ${selectedGroupLabel}.`
                  : "Select a folder, then select a log to load its content."}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div
                className="inline-flex rounded-lg border border-slate-700 bg-slate-900/80 p-1"
                role="group"
                aria-label="Log order"
              >
                <button
                  type="button"
                  onClick={() => setLogOrder(LOG_ORDER.EARLIEST)}
                  aria-pressed={logOrder === LOG_ORDER.EARLIEST}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition ${
                    logOrder === LOG_ORDER.EARLIEST
                      ? "bg-sky-500/15 text-sky-100 shadow-[0_0_0_1px_rgba(56,189,248,0.24)]"
                      : "text-slate-300 hover:text-slate-50"
                  }`}
                >
                  Earliest
                </button>
                <button
                  type="button"
                  onClick={() => setLogOrder(LOG_ORDER.LATEST)}
                  aria-pressed={logOrder === LOG_ORDER.LATEST}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition ${
                    logOrder === LOG_ORDER.LATEST
                      ? "bg-sky-500/15 text-sky-100 shadow-[0_0_0_1px_rgba(56,189,248,0.24)]"
                      : "text-slate-300 hover:text-slate-50"
                  }`}
                >
                  Latest
                </button>
              </div>
              <button
                type="button"
                onClick={handleRefreshSelected}
                disabled={!selectedGroup || !selectedCollection || detailLoading || actionLoading}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-900/80 px-3 text-sm font-medium text-slate-200 transition hover:border-sky-500/40 hover:text-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaSyncAlt className={detailLoading ? "animate-spin" : ""} />
                Refresh
              </button>
              <button
                type="button"
                onClick={handleDeleteSelected}
                disabled={!selectedGroup || !selectedCollection || !selectedLog || actionLoading}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 text-sm font-medium text-rose-100 transition hover:border-rose-400 hover:bg-rose-500/15 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaTrashAlt />
                Delete file
              </button>
            </div>
          </div>

          <div className="px-4 py-4">
            {confirmingDelete ? (
              <div className="mb-4 flex flex-col gap-3 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <FaExclamationTriangle className="mt-0.5 shrink-0 text-amber-300" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-amber-50">
                      Delete this log file?
                    </div>
                    <div className="mt-1 text-sm text-amber-100/85">
                      This will remove {selectedCollectionLabel} from {selectedGroupLabel}.
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    disabled={actionLoading}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-900/80 px-3 text-sm font-medium text-slate-200 transition hover:border-slate-600 hover:text-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FaTimes />
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    disabled={actionLoading}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-rose-400/40 bg-rose-500/15 px-3 text-sm font-medium text-rose-50 transition hover:border-rose-300 hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FaTrashAlt />
                    {actionLoading ? "Deleting..." : "Delete now"}
                  </button>
                </div>
              </div>
            ) : null}

            <form
              onSubmit={handleSearchSubmit}
              className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <label className="relative flex-1">
                <span className="sr-only">Search within the selected log</span>
                <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search within the selected log"
                  disabled={!selectedLog && !detailLoading}
                  className="h-11 w-full rounded-lg border border-slate-800 bg-slate-950/80 pl-9 pr-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/15 disabled:cursor-not-allowed disabled:opacity-60"
                />
              </label>

              <button
                type="submit"
                disabled={!selectedLog || detailLoading}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 text-sm font-medium text-sky-100 transition hover:border-sky-400 hover:bg-sky-500/15 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Search
              </button>

              <button
                type="button"
                onClick={handleResetSearch}
                disabled={!searchInput && !appliedSearch}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-900/80 px-4 text-sm font-medium text-slate-200 transition hover:border-slate-600 hover:text-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Reset
              </button>
            </form>

            <div className="max-h-[calc(100vh-22rem)] overflow-auto rounded-xl border border-slate-800 bg-slate-950/80">
              {detailLoading ? (
                <div className="flex min-h-[24rem] items-center justify-center px-4 py-10 text-sm text-slate-500">
                  Loading selected log...
                </div>
              ) : selectedCollection ? (
                selectedLog ? (
                  displayedContent ? (
                    <pre className="whitespace-pre-wrap break-words px-4 py-4 font-mono text-xs leading-6 text-slate-200">
                      {displayedContent}
                    </pre>
                  ) : isSearchActive ? (
                    <div className="flex min-h-[24rem] items-center justify-center px-4 py-10 text-sm text-slate-500">
                      No lines match the current search.
                    </div>
                  ) : (
                    <div className="flex min-h-[24rem] items-center justify-center px-4 py-10 text-sm text-slate-500">
                      This log is empty.
                    </div>
                  )
                ) : (
                  <div className="flex min-h-[24rem] items-center justify-center px-4 py-10 text-sm text-slate-500">
                    The selected log could not be loaded.
                  </div>
                )
              ) : selectedGroup ? (
                <div className="flex min-h-[24rem] items-center justify-center px-4 py-10 text-sm text-slate-500">
                  Select a log file from the selected folder to view its contents.
                </div>
              ) : (
                <div className="flex min-h-[24rem] items-center justify-center px-4 py-10 text-sm text-slate-500">
                  Select a log folder, then choose a log file to view its contents.
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminLogs;
